import React, { useMemo } from "react";
import { productionApi } from "../../../services/reportsService";
import { useReportData } from "../useReportData";
import ServerTable from "../ServerTable";
import {
  AlertStrip,
  BarList,
  ColumnChart,
  DonutChart,
  ErrorState,
  ItemList,
  KpiGrid,
  Leaderboard,
  Panel,
  ReportSkeleton,
  Row,
  SeriesLegend,
  StatusBadge,
  TablePanel,
  DistTable,
  TrendChart,
  TrendTable,
  cols,
  useReportContext,
} from "../ReportKit";
import {
  PALETTE,
  buildKpis,
  formatDate,
  formatNumber,
  getPreviousRange,
  groupCount,
  parseDate,
  toDateString,
} from "../reportUtils";

// The endpoint has no summary, so the numbers are worked out from the latest finished jobs.
const SAMPLE_SIZE = 200; // the endpoint only allows page sizes from 1 to 200

const DEFS = [
  {
    key: "finishedJobs",
    label: "Finished jobs",
    desc: "Jobs completed {p}",
    prev: "previousFinishedJobs",
    icon: "mdi:check-all",
  },
  {
    key: "units",
    label: "Units made",
    desc: "Total quantity in these jobs",
    icon: "mdi:package-variant-closed",
  },
  {
    key: "orders",
    label: "Orders covered",
    desc: "Different orders these jobs belong to",
    icon: "mdi:clipboard-text-outline",
  },
  {
    key: "clients",
    label: "Clients served",
    desc: "Different clients these jobs are for",
    icon: "mdi:account-group-outline",
  },
  {
    key: "avgHours",
    label: "Time per job",
    desc: "Average time from start to finish",
    unit: "HOURS",
    good: "DOWN",
    icon: "mdi:clock-outline",
  },
  {
    key: "qcRate",
    label: "Quality passed",
    desc: "Out of every 100 checked, how many passed",
    unit: "PERCENT",
    icon: "mdi:shield-check-outline",
  },
  {
    key: "returned",
    label: "Sent back",
    desc: "Jobs that had to be done again",
    good: "DOWN",
    icon: "mdi:undo-variant",
  },
  {
    key: "late",
    label: "Finished late",
    desc: "Done after the promised delivery date",
    good: "DOWN",
    icon: "mdi:alarm-light-outline",
  },
];

const hoursBetween = (a, b) => {
  if (!a || !b) return null;
  const h = (new Date(b) - new Date(a)) / 36e5;
  return Number.isFinite(h) && h >= 0 ? h : null;
};
const duration = (h) =>
  h === null || h === undefined
    ? "—"
    : h < 1
      ? `${Math.round(h * 60)}m`
      : h < 48
        ? `${formatNumber(h, 1)}h`
        : `${formatNumber(h / 24, 1)}d`;
const isPass = (q) => /PASS|APPROV/i.test(q || "");
const isFail = (q) => /FAIL|REJECT/i.test(q || "");
const avg = (list) =>
  list.length ? list.reduce((s, n) => s + n, 0) / list.length : 0;

const summarize = (rows, total, prevTotal, dates) => {
  const jobs = rows.map((r) => ({
    ...r,
    hours: hoursBetween(r.operationsStartedAt, r.operationsEndedAt),
    late: !!(
      r.expectedDeliveryDate &&
      r.operationsEndedAt &&
      toDateString(new Date(r.operationsEndedAt)) >
        String(r.expectedDeliveryDate).slice(0, 10)
    ),
  }));
  const hours = jobs.map((j) => j.hours).filter((h) => h !== null);
  const pass = jobs.filter((j) => isPass(j.qcStatus)).length;
  const fail = jobs.filter((j) => isFail(j.qcStatus)).length;

  const perDay = new Map();
  jobs.forEach((j) => {
    if (j.operationsEndedAt) {
      const k = toDateString(new Date(j.operationsEndedAt));
      perDay.set(k, (perDay.get(k) || 0) + 1);
    }
  });
  let trend = [...perDay]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([bucket, finished]) => ({ bucket, finished }));
  const s = parseDate(dates.startDate);
  const e = parseDate(dates.endDate);
  if ((e - s) / 864e5 <= 92) {
    trend = [];
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const k = toDateString(d);
      trend.push({ bucket: k, finished: perDay.get(k) || 0 });
    }
  }

  const people = new Map();
  jobs.forEach((j) => {
    const n = j.assignedEmployeeName || "Unassigned";
    const p = people.get(n) || {
      name: n,
      jobs: 0,
      units: 0,
      pass: 0,
      fail: 0,
      hours: [],
    };
    p.jobs += 1;
    p.units += Number(j.quantity) || 0;
    if (isPass(j.qcStatus)) p.pass += 1;
    if (isFail(j.qcStatus)) p.fail += 1;
    if (j.hours !== null) p.hours.push(j.hours);
    people.set(n, p);
  });
  const byEmployee = [...people.values()]
    .map((p) => ({
      ...p,
      qc: p.pass + p.fail ? (p.pass / (p.pass + p.fail)) * 100 : null,
      avgHours: avg(p.hours) || null,
    }))
    .sort((a, b) => b.jobs - a.jobs);

  const bucketDefs = [
    ["Under 1h", 0, 1],
    ["1 to 4h", 1, 4],
    ["4 to 8h", 4, 8],
    ["8 to 24h", 8, 24],
    ["Over 24h", 24, Infinity],
  ];
  return {
    jobs,
    trend,
    byEmployee,
    sampled: rows.length < total,
    raw: {
      finishedJobs: total,
      previousFinishedJobs: prevTotal,
      units: jobs.reduce((n, j) => n + (Number(j.quantity) || 0), 0),
      orders: new Set(jobs.map((j) => j.purchaseOrderId).filter(Boolean)).size,
      clients: new Set(jobs.map((j) => j.clientName).filter(Boolean)).size,
      avgHours: avg(hours),
      qcRate: pass + fail ? (pass / (pass + fail)) * 100 : 0,
      returned: jobs.filter((j) => j.returned).length,
      late: jobs.filter((j) => j.late).length,
    },
    byStage: groupCount(jobs, (j) => j.operationStageName, 10),
    byProduct: groupCount(jobs, (j) => j.productName, 10),
    byClient: groupCount(jobs, (j) => j.clientName, 10),
    qc: groupCount(jobs, (j) => j.qcStatus || "Not checked"),
    orderStatus: groupCount(jobs, (j) => j.orderStatus),
    buckets: bucketDefs.map(([name, lo, hi]) => ({
      name,
      key: name,
      value: hours.filter((h) => h >= lo && h < hi).length,
    })),
    slowest: [...jobs]
      .filter((j) => j.hours !== null)
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 8),
  };
};

const CompletedOperationsView = ({ params, refreshKey }) => {
  const { view } = useReportContext();
  const dates = useMemo(
    () => ({ startDate: params.startDate, endDate: params.endDate }),
    [params.startDate, params.endDate],
  );
  const { data, loading, error, reload } = useReportData(async () => {
    const [cur, prev] = await Promise.all([
      productionApi.completedOperations({
        ...dates,
        page: 0,
        size: SAMPLE_SIZE,
        sort: "operationsEndedAt,desc",
      }),
      productionApi
        .completedOperations({ ...getPreviousRange(dates), page: 0, size: 1 })
        .catch(() => null),
    ]);
    return {
      rows: cur?.content || [],
      total: cur?.totalElements ?? (cur?.content || []).length,
      prevTotal: prev?.totalElements ?? null,
    };
  }, [dates.startDate, dates.endDate, refreshKey]);

  const sum = useMemo(
    () =>
      data ? summarize(data.rows, data.total, data.prevTotal, dates) : null,
    [data, dates],
  );

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data || !sum) return <ReportSkeleton />;

  const kpis = buildKpis(sum.raw, DEFS);
  const perDay = [
    { key: "finished", label: "Finished jobs", color: PALETTE[1], type: "bar" },
  ];

  const columns = [
    {
      key: "productName",
      header: "Product",
      render: (v) => <strong>{v}</strong>,
    },
    { key: "operationStageName", header: "Stage" },
    { key: "purchaseOrderNumber", header: "Order" },
    { key: "clientName", header: "Client" },
    cols.num("quantity", "Quantity"),
    cols.num("price", "Price", "DECIMAL"),
    cols.num("grandTotal", "Order total", "DECIMAL"),
    { key: "assignedEmployeeName", header: "Done by" },
    {
      key: "qcStatus",
      header: "Quality",
      render: (v) => <StatusBadge value={v} />,
    },
    {
      key: "orderStatus",
      header: "Order status",
      render: (v) => <StatusBadge value={v} />,
    },
    {
      key: "operationsStartedAt",
      header: "Started",
      render: (v) => formatDate(v, true),
    },
    {
      key: "operationsEndedAt",
      header: "Finished",
      sortable: true,
      render: (v) => formatDate(v, true),
    },
    {
      key: "timeTaken",
      header: "Time taken",
      align: "right",
      render: (_, r) =>
        duration(hoursBetween(r.operationsStartedAt, r.operationsEndedAt)),
      exportValue: (r) =>
        hoursBetween(r.operationsStartedAt, r.operationsEndedAt),
    },
    {
      key: "expectedDeliveryDate",
      header: "Promised date",
      render: (v) => formatDate(v),
    },
    { key: "returned", header: "Sent back", render: (v) => (v ? "Yes" : "No") },
    { key: "remarks", header: "Remarks" },
  ];
  const jobItem = (j) => ({
    id: j.operationStageHistoryId,
    title: j.productName,
    subtitle: [j.operationStageName, j.assignedEmployeeName, j.clientName]
      .filter(Boolean)
      .join(", "),
    badge: j.qcStatus,
    right: formatDate(j.operationsEndedAt, true),
  });

  const L = (title, how, rows) => ({
    modal: {
      title,
      how,
      columns,
      rows,
      note: sum.sampled
        ? `Based on the latest ${data.rows.length} finished jobs.`
        : "",
    },
  });
  const byText = (key) => (a, b) =>
    String(a[key] || "").localeCompare(String(b[key] || ""));
  const details = {
    finishedJobs: {
      scrollTo: "tbl-main",
      modal: {
        title: "All finished jobs",
        how: "These are all the jobs finished {p}.",
        columns,
        fetcher: productionApi.completedOperations,
        params: dates,
        sort: "operationsEndedAt,desc",
      },
    },
    units: L(
      "Jobs and their quantity",
      "Add up the quantity of these jobs to get the units made.",
      sum.jobs,
    ),
    orders: L(
      "Jobs by order",
      "These jobs belong to the orders counted here.",
      [...sum.jobs].sort(byText("purchaseOrderNumber")),
    ),
    clients: L(
      "Jobs by client",
      "These jobs are for the clients counted here.",
      [...sum.jobs].sort(byText("clientName")),
    ),
    avgHours: L(
      "Jobs with their time",
      "Each job took some time from start to finish. The average is shown.",
      [...sum.jobs]
        .filter((j) => j.hours !== null)
        .sort((a, b) => b.hours - a.hours),
    ),
    qcRate: L(
      "Quality checks",
      "These jobs have a quality result. The number shows how many of the checked jobs passed.",
      sum.jobs.filter((j) => j.qcStatus),
    ),
    returned: L(
      "Jobs sent back",
      "These jobs had to be done again.",
      sum.jobs.filter((j) => j.returned),
    ),
    late: L(
      "Jobs finished late",
      "These jobs were finished after the promised delivery date.",
      sum.jobs.filter((j) => j.late),
    ),
  };

  return (
    <div className={`rp-view ${loading ? "rp-refreshing" : ""}`}>
      <AlertStrip
        items={[
          sum.sampled && {
            tone: "info",
            icon: "mdi:information-outline",
            text: `Charts and most numbers use your latest ${formatNumber(data.rows.length, 0)} finished jobs out of ${formatNumber(data.total, 0)}. "Finished jobs" and the table include all of them.`,
          },
          sum.raw.late > 0 && {
            tone: "bad",
            icon: "mdi:alarm-light-outline",
            text: `${sum.raw.late} jobs were finished after the promised delivery date`,
          },
        ]}
      />
      <KpiGrid kpis={kpis} details={details} />

      {view === "table" ? (
        <>
          <ServerTable
            id="tbl-main"
            title="Completed jobs"
            columns={columns}
            params={dates}
            fetcher={productionApi.completedOperations}
            searchable={false}
            defaultSort="operationsEndedAt,desc"
            pageSize={10}
            exportName="completed-jobs"
          />
          <div className="rp-tables">
            <TrendTable
              title="Jobs finished by date"
              data={sum.trend}
              series={perDay}
              granularity="DAY"
            />
            <DistTable
              title="Jobs by stage"
              data={sum.byStage}
              nameHeader="Stage"
              valueHeader="Jobs"
            />
            <TablePanel
              title="Employee output"
              exportName="employees"
              columns={[
                cols.text("name", "Person"),
                cols.num("jobs", "Jobs"),
                cols.num("units", "Units"),
                cols.num("qc", "Quality passed", "PERCENT"),
                cols.num("avgHours", "Average time", "HOURS"),
              ]}
              rows={sum.byEmployee}
            />
            <DistTable
              title="Quality results"
              data={sum.qc}
              nameHeader="Result"
              valueHeader="Jobs"
            />
            <DistTable
              title="Order status"
              data={sum.orderStatus}
              nameHeader="Status"
              valueHeader="Jobs"
            />
            <DistTable
              title="By product"
              data={sum.byProduct}
              nameHeader="Product"
              valueHeader="Jobs"
            />
            <DistTable
              title="By client"
              data={sum.byClient}
              nameHeader="Client"
              valueHeader="Jobs"
            />
            <DistTable
              title="Time taken per job"
              data={sum.buckets}
              nameHeader="Time"
              valueHeader="Jobs"
            />
            <TablePanel
              title="Took the longest"
              exportName="slowest-jobs"
              columns={[
                cols.text("productName", "Product"),
                cols.text("operationStageName", "Stage"),
                cols.text("assignedEmployeeName", "Done by"),
                cols.num("hours", "Time taken", "HOURS"),
              ]}
              rows={sum.slowest}
            />
          </div>
        </>
      ) : (
        <>
          <Row layout="2-1">
            <Panel
              title="Jobs finished each day"
              actions={<SeriesLegend series={perDay} />}
            >
              <TrendChart data={sum.trend} series={perDay} granularity="DAY" />
            </Panel>
            <Panel title="Quality results">
              <DonutChart data={sum.qc} />
            </Panel>
          </Row>
          <Row layout="1-1">
            <Panel title="Jobs by stage">
              <BarList data={sum.byStage} color={PALETTE[0]} />
            </Panel>
            <Panel title="Order status">
              <DonutChart data={sum.orderStatus} />
            </Panel>
          </Row>
          <Row layout="2-1">
            <Panel title="Employee output" subtitle="Finished jobs per person">
              <Leaderboard
                primaryLabel="jobs"
                rows={sum.byEmployee.map((p) => ({
                  name: p.name,
                  primary: p.jobs,
                  metrics: [
                    { label: "Units", value: p.units },
                    { label: "Quality passed", value: p.qc, unit: "PERCENT" },
                    { label: "Average time", value: p.avgHours, unit: "HOURS" },
                  ],
                }))}
              />
            </Panel>
            <Panel title="Time taken per job">
              <ColumnChart data={sum.buckets} color={PALETTE[2]} />
            </Panel>
          </Row>
          <Row layout="1-1">
            <Panel title="By product">
              <BarList data={sum.byProduct} color={PALETTE[3]} />
            </Panel>
            <Panel title="By client">
              <BarList data={sum.byClient} color={PALETTE[5]} />
            </Panel>
          </Row>
          <Row layout="1-1">
            <Panel title="Latest finished jobs">
              <ItemList
                empty="No finished jobs in this period"
                items={sum.jobs.slice(0, 8).map(jobItem)}
              />
            </Panel>
            <Panel
              title="Took the longest"
              subtitle="Longest time from start to finish"
            >
              <ItemList
                empty="No timing data yet"
                items={sum.slowest.map((j) => ({
                  ...jobItem(j),
                  right: duration(j.hours),
                }))}
              />
            </Panel>
          </Row>
        </>
      )}
    </div>
  );
};

export default CompletedOperationsView;
